import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject } from '@angular/core';

import { ThemeService } from './theme.service';
import type { GrChartBaseOptions, GrChartPalette, GrChartSeriesColor } from '../types';

const PALETTE_TOKEN_MAP: Record<keyof GrChartPalette, string> = {
  brand: '--brand',
  brandSoft: '--brand-soft',
  brandStrong: '--brand-strong',
  success: '--success',
  warning: '--warning',
  danger: '--danger',
  info: '--info',
  neutral: '--text-muted',
  positive: '--success',
  neutralRating: '--text-muted',
  negative: '--danger',
  textPrimary: '--text-primary',
  textSecondary: '--text-secondary',
  textMuted: '--text-muted',
  surfaceCard: '--surface-card',
  surfaceApp: '--surface-app',
  border: '--border-on-light',
  gridLine: '--border-on-light',
  annotationBg: '--surface-card',
  annotationText: '--text-primary',
  annotationBorder: '--border-on-light',
  tooltipBg: '--surface-elevated',
  tooltipText: '--text-on-dark',
  fontFamily: '--font-family-base',
  fontSizeXs: '--font-size-xs',
  fontSizeSm: '--font-size-sm',
  fontSize2xl: '--font-size-2xl',
};

@Injectable({ providedIn: 'root' })
export class ChartThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly themeService = inject(ThemeService);

  readonly palette = computed<GrChartPalette>(() => {
    this.themeService.isDark();
    return this.readPalette();
  });

  readonly baseOptions = computed<GrChartBaseOptions>(() => {
    const p = this.palette();
    return {
      chart: {
        fontFamily: p.fontFamily,
        foreColor: p.textSecondary,
        background: 'transparent',
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: true, speed: 320, animateGradually: { enabled: false } },
      },
      grid: {
        borderColor: p.gridLine,
        strokeDashArray: 4,
        padding: { left: 8, right: 8, top: 48, bottom: 0 },
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'center',
        fontSize: p.fontSizeSm,
        fontWeight: 500,
        labels: { colors: p.textSecondary },
        markers: { size: 6, strokeWidth: 0, offsetX: -4 },
        itemMargin: { horizontal: 14, vertical: 4 },
      },
      tooltip: {
        theme: this.themeService.isDark() ? 'dark' : 'light',
        style: { fontSize: p.fontSizeSm },
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: {
        labels: { style: { colors: p.textMuted, fontSize: p.fontSizeXs } },
        axisBorder: { color: p.border },
        axisTicks: { color: p.border },
      },
      yaxis: {
        labels: { style: { colors: p.textMuted, fontSize: p.fontSizeXs } },
      },
    };
  });

  resolveColor(token: GrChartSeriesColor | string): string {
    if (token.startsWith('#')) return token;
    return this.palette()[token as GrChartSeriesColor];
  }

  resolveColors(tokens: readonly GrChartSeriesColor[]): string[] {
    const p = this.palette();
    return tokens.map((t) => p[t]);
  }

  private readPalette(): GrChartPalette {
    const view = this.document.defaultView;
    const root = this.document.documentElement;
    if (!view || !root) {
      return Object.fromEntries(
        Object.keys(PALETTE_TOKEN_MAP).map((k) => [k, '']),
      ) as unknown as GrChartPalette;
    }
    const styles = view.getComputedStyle(root);
    const palette = {} as GrChartPalette;
    for (const [key, token] of Object.entries(PALETTE_TOKEN_MAP) as [
      keyof GrChartPalette,
      string,
    ][]) {
      palette[key] = styles.getPropertyValue(token).trim();
    }
    return palette;
  }
}
